import type { Category, EventId, PlanEvent } from "../../../shared/types";

export interface TemplateSlot {
  readonly eventId: EventId;
  readonly dayOfWeek: number;
  readonly title: string;
  readonly category: Category;
  readonly startTime: string;
  readonly endTime: string;
  readonly recurrenceType: "daily" | "weekly" | "monthly" | "quarterly" | "semiannual" | "yearly";
  readonly interval: number;
  readonly tags: readonly string[];
  readonly description: string;
}

export interface TemplateData {
  readonly daily: readonly TemplateSlot[];
  readonly weekly: ReadonlyMap<number, readonly TemplateSlot[]>;
  readonly monthly: readonly TemplateSlot[];
  readonly quarterly: readonly TemplateSlot[];
  readonly semiannual: readonly TemplateSlot[];
  readonly yearly: readonly TemplateSlot[];
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function toSlot(event: PlanEvent, dayOfWeek: number): TemplateSlot {
  return {
    eventId: event.id,
    dayOfWeek,
    title: event.title,
    category: event.category,
    startTime: event.startTime,
    endTime: event.endTime,
    recurrenceType: event.recurrence!.type,
    interval: event.recurrence!.interval,
    tags: event.tags,
    description: event.description,
  };
}

function sortByTime(slots: TemplateSlot[]): TemplateSlot[] {
  return slots.sort(
    (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
  );
}

export function deriveTemplate(events: readonly PlanEvent[]): TemplateData {
  const daily: TemplateSlot[] = [];
  const weeklyMap = new Map<number, TemplateSlot[]>();
  const monthly: TemplateSlot[] = [];
  const quarterly: TemplateSlot[] = [];
  const semiannual: TemplateSlot[] = [];
  const yearly: TemplateSlot[] = [];

  for (let d = 0; d < 7; d++) {
    weeklyMap.set(d, []);
  }

  for (const event of events) {
    if (!event.recurrence) continue;
    if (event.detachedFromId) continue;

    const { type } = event.recurrence;

    if (type === "daily") {
      daily.push(toSlot(event, 0));
    } else if (type === "weekly") {
      // If daysOfWeek is specified, use those days; otherwise use event date
      const days = event.recurrence.daysOfWeek ?? [new Date(event.date + "T00:00:00").getDay()];
      for (const dayOfWeek of days) {
        weeklyMap.get(dayOfWeek)?.push(toSlot(event, dayOfWeek));
      }
    } else if (type === "monthly") {
      const dayOfWeek = new Date(event.date + "T00:00:00").getDay();
      monthly.push(toSlot(event, dayOfWeek));
    } else if (type === "quarterly") {
      const dayOfWeek = new Date(event.date + "T00:00:00").getDay();
      quarterly.push(toSlot(event, dayOfWeek));
    } else if (type === "semiannual") {
      const dayOfWeek = new Date(event.date + "T00:00:00").getDay();
      semiannual.push(toSlot(event, dayOfWeek));
    } else if (type === "yearly") {
      const dayOfWeek = new Date(event.date + "T00:00:00").getDay();
      yearly.push(toSlot(event, dayOfWeek));
    }
  }

  sortByTime(daily);
  for (const [, slots] of weeklyMap) {
    sortByTime(slots);
  }
  sortByTime(monthly);
  sortByTime(quarterly);
  sortByTime(semiannual);
  sortByTime(yearly);

  return { daily, weekly: weeklyMap, monthly, quarterly, semiannual, yearly };
}

export const DAY_LABELS: Record<number, string> = {
  0: "Dimanche",
  1: "Lundi",
  2: "Mardi",
  3: "Mercredi",
  4: "Jeudi",
  5: "Vendredi",
  6: "Samedi",
};

export const DAY_SHORT_LABELS: Record<number, string> = {
  0: "Dim",
  1: "Lun",
  2: "Mar",
  3: "Mer",
  4: "Jeu",
  5: "Ven",
  6: "Sam",
};

/** Monday-first order: 1,2,3,4,5,6,0 */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;
